import { useEffect, useState } from 'react';

export function useRows<T = any>(initialValue: T[] = [], onChange?: (rows: T[]) => void) {
  const [internalRows, setInternalRows] = useState<T[]>(() => {
    if (Array.isArray(initialValue)) {
      return initialValue;
    }
    return [{} as T];
  });

  useEffect(() => {
    setInternalRows(initialValue);
  }, [initialValue]);

  const handleAddRow = () => {
    const newRows = [...internalRows, {} as T];
    setInternalRows(newRows);
    onChange?.(newRows);
  };

  const handleDeleteRow = (selectedRows: number[]) => {
    if (selectedRows.length === 0) return;

    const newRows = internalRows.filter((_, index) => !selectedRows.includes(index));

    // If all rows are deleted, add an empty row
    if (newRows.length === 0) {
      newRows.push({} as T);
    }

    setInternalRows(newRows);
    onChange?.(newRows);
  };

  // Store the copied row data
  const [copiedRow, setCopiedRow] = useState<T | null>(null);

  const handleCopyRow = (selectedRow: number) => {
    // If no valid row is selected, do nothing
    if (isNaN(selectedRow) || selectedRow < 0 || selectedRow >= internalRows.length) return;

    // Store the copied row data
    setCopiedRow({ ...internalRows[selectedRow] });
  };

  const handlePasteRow = (targetRow: number) => {
    // If no valid row is selected or no copied data, do nothing
    if (isNaN(targetRow) || targetRow < 0 || targetRow >= internalRows.length) return;
    if (!copiedRow) return;

    // Create a new array with the pasted row
    const newRows = [...internalRows];
    newRows[targetRow] = { ...copiedRow };

    setInternalRows(newRows);
    onChange?.(newRows);
  };

  const handleMoveRow = (selectedRow: number, direction: 'up' | 'down') => {
    // If no valid row is selected, do nothing
    if (isNaN(selectedRow) || selectedRow < 0 || selectedRow >= internalRows.length) return;

    // Calculate the target index based on the direction
    const targetIndex = direction === 'up' ? selectedRow - 1 : selectedRow + 1;

    // Check if the target index is valid
    if (targetIndex < 0 || targetIndex >= internalRows.length) return;

    // Create a new array with the moved row
    const newRows = [...internalRows];

    // Swap the rows
    [newRows[selectedRow], newRows[targetIndex]] = [newRows[targetIndex], newRows[selectedRow]];

    setInternalRows(newRows);
    onChange?.(newRows);
  };

  return {
    internalRows,
    setInternalRows,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    hasCopiedRow: copiedRow !== null
  };
}
